import type { BoardType } from "@/model/baseTypes";
import { isPlanningBoard } from "@/model/board";
import type { BoardCard } from "@/model/card";
import { linkColors, statusClassColors } from "@/model/colors";
import type { Link } from "@/model/link";
import { minFaded } from "@/model/markMode";
import { isDependency } from "@/model/stickyType";
import { useBoardStore } from "@/store/board";
import { useClientSettingsStore } from "@/store/clientSettings";
import { riskyLinkStates } from "@/store/helpers/linkState";
import type { LinkedCards } from "@/store/link";
import { useLinkStore } from "@/store/link";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useWorkModeStore } from "@/store/workMode";

export function showLink(linked: LinkedCards) {
  const markMode = showIcon(linked.link)
    ? "normal"
    : minFaded(linked.from.meta.mark, linked.to.meta.mark);
  const search = useSearchMenuStore();
  return (
    !isHidden(linked.from) &&
    !isHidden(linked.to) &&
    ((useBoardStore().baseBoard.showPermanentLinks && markMode !== "fade-out") ||
      search.showCardLinks(linked.from) ||
      search.showCardLinks(linked.to) ||
      search.matchesLinkStates(useLinkStore().linkState(linked.link))) &&
    isStateShown(linked.link)
  );
}

export function showIcon(link: Link): boolean {
  const state = useLinkStore().linkState(link);
  return (
    useBoardStore().showRiskyLinks &&
    useSearchMenuStore().matchesLinkStates(state) &&
    riskyLinkStates.has(state)
  );
}

/**
 * Collects IDs of dependency cards whose enabling team work items are all done.
 * Only applies on planning boards in execution mode (statusDistribution is not fetched in planning mode).
 */
export function readyDependencyIds(boardType: BoardType): Set<string> {
  const result = new Set<string>();
  if (
    !isPlanningBoard(boardType) ||
    !useClientSettingsStore().highlightDoneLinks ||
    !useWorkModeStore().isExecutionMode
  ) {
    return result;
  }
  for (const card of Object.values(useBoardStore().currentBoard().cards)) {
    if (isDependency(card.data) && isDependencyReady(card)) {
      result.add(card.data.id);
    }
  }
  return result;
}

/** Determines the color of a link: risky state color > green for blocked team links to ready deps > default. */
export function linkColor(
  isRisky: boolean,
  linked: LinkedCards,
  readyDeps: Set<string>,
  defaultColor: string,
) {
  if (isRisky) return linkColors[useLinkStore().linkState(linked.link)];
  if (isBlockedTeamLink(linked, readyDeps)) return statusClassColors.done;
  return defaultColor;
}

/** A dependency is ready when all items in its statusDistribution are in the "done" state. */
function isDependencyReady(dep: BoardCard) {
  const dist = dep.data.statusDistribution;
  if (!dist || dist.size === 0) return false;
  const withItems = [...dist.entries()].filter(([, { value }]) => value > 0);
  return withItems.length > 0 && withItems.every(([statusClass]) => statusClass === "done");
}

/** Checks if the link connects a ready dependency to a card from the blocked team (precondTeam). */
function isBlockedTeamLink(linked: LinkedCards, readyDeps: Set<string>) {
  const { from, to } = linked;
  if (readyDeps.has(from.data.id)) {
    return to.data.teamId === from.data.precondTeam?.id;
  }
  if (readyDeps.has(to.data.id)) {
    return from.data.teamId === to.data.precondTeam?.id;
  }
  return false;
}

function isHidden(c: BoardCard) {
  return c.meta.mark === "hidden";
}

function isStateShown(link: Link) {
  return (
    !useBoardStore().showRiskyLinks ||
    useSearchMenuStore().matchesLinkStates(useLinkStore().linkState(link), true)
  );
}
